'use client';
import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import {
  BookOpen,
  Search,
  Play,
  Clock,
  ChevronRight,
  Sparkles,
  Image,
  Palette,
  CreditCard,
  User,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const ICON_MAP: Record<string, React.ElementType> = {
  sparkles: Sparkles,
  image: Image,
  palette: Palette,
  creditCard: CreditCard,
  user: User,
};

const LEVEL_COLORS: Record<string, string> = {
  beginner: 'bg-green-500/10 text-green-500',
  intermediate: 'bg-yellow-500/10 text-yellow-500',
  advanced: 'bg-primary/10 text-primary',
};

export default function LearnCenterContent() {
  const { t } = useLanguage();
  const lp = t?.learnCenterPage;
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeLevel, setActiveLevel] = useState('all');
  const [search, setSearch] = useState('');
  const [openGuideId, setOpenGuideId] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<number | null>(0);

  const guides = lp?.guides ?? [];
  const categoryKeys = lp?.categoryKeys ?? [];

  const categoryTabs = useMemo(
    () => [
      { key: 'all', label: lp?.categoryAll ?? 'All' },
      ...categoryKeys.map((key) => ({
        key,
        label: lp?.categories?.[key as keyof typeof lp.categories] ?? key,
      })),
    ],
    [lp, categoryKeys]
  );

  const levelTabs = useMemo(
    () => [
      { key: 'all', label: lp?.levelAll ?? 'All Levels' },
      { key: 'beginner', label: lp?.levels?.beginner ?? 'Beginner' },
      { key: 'intermediate', label: lp?.levels?.intermediate ?? 'Intermediate' },
      { key: 'advanced', label: lp?.levels?.advanced ?? 'Advanced' },
    ],
    [lp]
  );

  const filtered = guides.filter((g) => {
    const matchCat = activeCategory === 'all' || g.categoryKey === activeCategory;
    const matchLevel = activeLevel === 'all' || g.levelKey === activeLevel;
    const haystack = `${g.title} ${g.desc}`.toLowerCase();
    const matchSearch = search === '' || haystack.includes(search.toLowerCase());
    return matchCat && matchLevel && matchSearch;
  });

  const openGuide = guides.find((g) => g.id === openGuideId) ?? null;

  if (openGuide) {
    const GuideIcon = ICON_MAP[openGuide.icon] || Sparkles;
    const categoryLabel =
      lp?.categories?.[openGuide.categoryKey as keyof typeof lp.categories] ??
      openGuide.categoryKey;
    const levelLabel =
      lp?.levels?.[openGuide.levelKey as keyof typeof lp.levels] ?? openGuide.levelKey;
    return (
      <main className="min-h-screen bg-background pt-24 pb-20">
        <section className="max-w-screen-md mx-auto px-6 lg:px-10">
          <button
            type="button"
            onClick={() => {
              setOpenGuideId(null);
              setOpenSection(0);
            }}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ChevronRight size={15} className="rotate-180" /> {lp?.backToLearn}
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <GuideIcon size={18} className="text-primary" />
            </div>
            <div>
              <p className="text-xs font-600 uppercase tracking-widest text-primary">
                {categoryLabel}
              </p>
              <span
                className={`text-xs font-600 px-2 py-0.5 rounded-full ${
                  LEVEL_COLORS[openGuide.levelKey] ?? LEVEL_COLORS.beginner
                }`}
              >
                {levelLabel}
              </span>
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-800 text-foreground mb-3 leading-tight">
            {openGuide.title}
          </h1>
          <p className="text-muted-foreground text-sm mb-8 pb-6 border-b border-border flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {openGuide.duration}
            </span>
          </p>
          <div className="space-y-3">
            {openGuide.sections.map((section, i) => (
              <div key={i} className="glass-card rounded-xl border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenSection(openSection === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                >
                  <span className="text-sm font-700 text-foreground">{section.heading}</span>
                  {openSection === i ? (
                    <ChevronUp size={16} className="text-muted-foreground shrink-0 ml-4" />
                  ) : (
                    <ChevronDown size={16} className="text-muted-foreground shrink-0 ml-4" />
                  )}
                </button>
                {openSection === i && (
                  <div className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-4">
                    {section.body}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-10 pt-8 border-t border-border">
            <div className="rounded-2xl bg-muted/50 border border-border p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-sm font-700 text-foreground mb-1">{lp?.ctaTitle}</h2>
                <p className="text-xs text-muted-foreground">{lp?.ctaDesc}</p>
              </div>
              <Link
                href="/sign-up-login-screen"
                className="btn-primary px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 shrink-0"
              >
                <Play size={14} /> {lp?.ctaButton}
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const activeLevelLabel =
    levelTabs.find((l) => l.key === activeLevel)?.label ?? activeLevel;

  return (
    <main className="min-h-screen bg-background pt-24 pb-20">
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-12">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <BookOpen size={13} className="text-primary" />
            <span className="text-xs font-600 text-primary">{lp?.badge}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-5 leading-tight">
            {lp?.titleBefore}{' '}
            <span className="gradient-text">{lp?.titleHighlight}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">{lp?.subtitle}</p>
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder={lp?.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-muted border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-8 flex flex-wrap gap-4">
        <div className="flex flex-wrap gap-2">
          {categoryTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveCategory(tab.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-500 border transition-colors ${
                activeCategory === tab.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {levelTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveLevel(tab.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-500 border transition-colors ${
                activeLevel === tab.key
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <BookOpen size={20} className="text-muted-foreground" />
            </div>
            <p className="text-base font-600 text-foreground mb-1">{lp?.emptyTitle}</p>
            <p className="text-sm text-muted-foreground mb-4">
              {activeLevel !== 'all' && activeCategory === 'all' ? (lp?.emptyDescLevel ??'').replace('{level}', activeLevelLabel)
                : lp?.emptyDescGeneric}
            </p>
            <button
              type="button"
              onClick={() => {
                setActiveCategory('all');
                setActiveLevel('all');
                setSearch('');
              }}
              className="px-4 py-2 rounded-xl bg-primary/10 text-primary text-sm font-600 hover:bg-primary/20 transition-colors"
            >
              {lp?.clearFilters}
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((guide) => {
              const GuideIcon = ICON_MAP[guide.icon] || Sparkles;
              return (
                <div
                  key={guide.id}
                  className="glass-card rounded-xl p-5 border border-border flex flex-col"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <GuideIcon size={15} className="text-primary" />
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-600 ${
                        LEVEL_COLORS[guide.levelKey] ?? LEVEL_COLORS.beginner
                      }`}
                    >
                      {lp?.levels?.[guide.levelKey as keyof typeof lp.levels]}
                    </span>
                  </div>
                  <p className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-1.5">
                    {lp?.categories?.[guide.categoryKey as keyof typeof lp.categories]}
                  </p>
                  <h3 className="text-sm font-700 text-foreground mb-2 leading-snug">
                    {guide.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed flex-1 mb-4">
                    {guide.desc}
                  </p>
                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/50">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock size={11} />
                      {guide.duration}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenGuideId(guide.id);
                        setOpenSection(0);
                      }}
                      className="text-xs text-primary font-600 flex items-center gap-1 hover:gap-2 transition-all"
                    >
                      {lp?.readGuide} <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="rounded-2xl bg-muted/50 border border-border p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-primary" />
              <h2 className="text-base font-700 text-foreground">{lp?.ctaTitle}</h2>
            </div>
            <p className="text-sm text-muted-foreground">{lp?.ctaDesc}</p>
          </div>
          <Link
            href="/sign-up-login-screen"
            className="btn-primary px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 shrink-0"
          >
            <Play size={14} /> {lp?.ctaButton}
          </Link>
        </div>
      </section>
    </main>
  );
}
